import { makeAutoObservable } from 'mobx';
import GlobalNotification from '../../../component/common/snackbar';
import i18n from '../../../i18n';
import { Dish, Order } from '../../../models';
import { CurrentOrderData } from '../../../models/fixed/data-analysis';
import apiManage from '../../../request';
import {
    cloneOrderDetailTableRowFallback,
    mergeOrderDetailSummaryWithTableRow,
    parseDataAnalysisOrderDetailWithHead,
    resolveOrderDetailCheckIdQuery,
} from '../../../request/api/data-analysis';
import { WhenError } from '../../../request/http';
import { SortType, handleSort, validateCurrentDate } from '../../../constants/tool-order';
import axios, { Canceler } from 'axios';
import { MobxObjectsManager } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';

/** 即时订单表列 id → handleSort 第三参类型（金额/人数/枚举用 Number，时间用 Date，文案用 String） */
export const ORDER_DATA_TABLE_SORT_TYPES: Record<string, 'Date' | 'Number' | 'String'> = {
    order_head_id: 'Number',
    table_name: 'String',
    customer_num: 'Number',
    total_amount: 'Number',
    should_amount: 'Number',
    paid_amount: 'Number',
    order_start_time: 'Date',
    order_end_time: 'Date',
    kds_time: 'Date',
    open_employee_name: 'String',
    eat_type: 'Number',
};

/** 订单筛选类型：未结算 / 已结算 */
export type OrderDataFilterType = 'UNSETTLED' | 'SETTLED';

class OrderDataStore {
    // loading
    loading = true;
    /** 筛选类型：当前未结算订单 / 当前已结算订单，默认已结算（商家首选查看已结账流水） */
    filterType: OrderDataFilterType = 'SETTLED';
    // 即时订单数据
    data: CurrentOrderData = {
        // 未结堂食订单
        eatInArr: [],
        // 未结外卖订单
        eatOutArr: [],
        // 已结堂食订单
        hisEatInArr: [],
        // 已结外卖订单
        hisEatOutArr: []
    };
    // 详情弹窗
    isOpenOrderDetail = false
    /** 打开详情时仅用于请求参数的订单标识（列表行） */
    orderDetailData: Order = {}
    /** 详情摘要：默认列表行，接口 `order_head` 有值字段再覆盖 */
    orderDetailSummary: Order | null = null
    // 详情菜品
    orderDetailDish: Dish[] = []
    // 详情loading
    detailLoading = true
    // 错误信息
    errorMessage = ''
    // 排序
    sortKeyValue: {
        // 未结堂食订单排序
        eatInArr?: SortType;
        // 未结外卖订单排序
        eatOutArr?: SortType;
        // 已结堂食订单排序
        hisEatInArr?: SortType;
        // 已结外卖订单排序
        hisEatOutArr?: SortType;
    } = {}
    // 源数据
    dataSource: CurrentOrderData = {
        // 未结堂食订单
        eatInArr: [],
        // 未结外卖订单
        eatOutArr: [],
        // 已结堂食订单
        hisEatInArr: [],
        // 已结外卖订单
        hisEatOutArr: []
    };
    // 取消请求
    canceler: Canceler | null = null;

    constructor() {
        makeAutoObservable(this)
    }

    /**
     * 获取数据
     */
    async fetchData(stringId: string) {
        this.setLoading(true);
        this?.canceler?.();
        const cancelToken = new axios.CancelToken(Canceler => this.canceler = Canceler);
        const { startTime, endTime } = validateCurrentDate();
        try {
            const res = await apiManage.get_data_analysis_res_immediate_order({
                string_id: stringId,
                start: startTime,
                end: endTime
            }, { cancelToken });
            if (!res?.error) {
                /** 列表直接使用接口数据；旧版 `formatDataRate`/`totalMaxRate` 已无消费方，不再写入 */
                this.setData(res);
                this.setErrorMessage('');
            } else {
                const error = res as WhenError;
                if (error.message !== 'canceled') {
                    if (error?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_no_database_config'));
                    } else {
                        this.setErrorMessage(i18n.t('immediate_business_analysis_page_msg_fetch_immediate_order_info_failed'));
                    }
                    this.setData({
                        eatInArr: [],
                        eatOutArr: [],
                        hisEatInArr: [],
                        hisEatOutArr: []
                    });
                }
            }
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * 获取订单详情数据
     */
    async fetchDetailData() {
        this.setDetailLoading(true)
        const mainPageData = MobxObjectsManager.get(MainPageState);
        const trimmedCheckId = resolveOrderDetailCheckIdQuery(this.orderDetailData.check_id);
        const res = await apiManage.get_data_analysis_order_details({
            string_id: mainPageData.currentRestaurant?.string_id || '',
            order_head_id: `${this.orderDetailData.order_head_id || ''}`,
            ...(trimmedCheckId ? { check_id: trimmedCheckId } : {}),
            sort: 'ASC'
        });
        const { order, details } = parseDataAnalysisOrderDetailWithHead(res);
        if (!res?.error) {
            this.setOrderDetailSummary(
                mergeOrderDetailSummaryWithTableRow(this.orderDetailData, order)
            );
            this.setOrderDetailDish(details ?? []);
        } else {
            GlobalNotification.error(i18n.t('immediate_get_order_details_failed'));
        }
        this.setDetailLoading(false)
    }



    /**
     * 设置筛选类型（未结算 / 已结算）
     */
    setFilterType(value: OrderDataFilterType) {
        this.filterType = value;
    }

    /**
     * 更新数据
     * @param value
     */
    setData(value: CurrentOrderData) {
        this.data = value
        this.dataSource = { ...value }
    }

    /**
     * 更新loading
     * @param value
     */
    setLoading(value: boolean) {
        this.loading = value
    }

    /**
     * 打开详情弹窗
     * @param value
     */
    setOpenOrderDetail(value: boolean) {
        this.isOpenOrderDetail = value
        if (!value) {
            this.orderDetailSummary = null
            this.orderDetailDish = []
        }
    }

    /**
     * 打开详情时写入列表行；摘要先展示表格字段，接口返回后再合并覆盖。
     * @param value 列表行订单
     */
    setOrderDetailData(value: Order) {
        this.orderDetailData = value
        this.orderDetailSummary = cloneOrderDetailTableRowFallback(value)
    }

    /**
     * 详情摘要（接口 order_head）
     * @param value 映射后的 Order，无数据时为 null
     */
    setOrderDetailSummary(value: Order | null) {
        this.orderDetailSummary = value
    }

    /**
     * 详情菜品
     * @param value
     */
    setOrderDetailDish(value: Dish[]) {
        this.orderDetailDish = value
    }

    /**
     * 详情loading
     * @param value
     */
    setDetailLoading(value: boolean) {
        this.detailLoading = value
    }

    /**
     * 更新errorMessage
     * @param value
     */
    setErrorMessage(value: string) {
        this.errorMessage = value
    }

    /**
     * 更新排序
     * @param value
     */
    setEatInArrSort(value: SortType, key: keyof CurrentOrderData) {
        this.sortKeyValue[key] = value
    }

    /**
     * 排序
     * @param key
     */
    setDataSort(key: keyof CurrentOrderData) {
        const value = this.data[key]
        const sortValue = this.sortKeyValue[key]?.value
        const sortKey = this.sortKeyValue[key]?.key
        // 是否有排序
        if (sortValue && sortKey) {
            // 有排序：按列语义选 Number/String/Date，传入副本避免污染 dataSource
            const sortKind = ORDER_DATA_TABLE_SORT_TYPES[sortKey] ?? 'String';
            const valueSort = handleSort({ value: sortValue, key: sortKey }, [...value], sortKind) || [];
            this.data[key] = valueSort;
        } else {
            // 没有排序 用源数据
            this.data[key] = [...this.dataSource[key]]
        }
    }
}

export default OrderDataStore

